import "ag-charts-enterprise";
import { AgChartOptions } from "ag-charts-enterprise";
import { AgCharts } from "ag-charts-react";

import { close } from "@/components/icons/SVGIcons";
import { Button } from "@/components/UI/Button";
import DraggableAnalytics from "@/lib/DraggableAnalytics";
import DroppableAnalytics from "@/lib/DroppableAnalytics";
import { IBaseAnalyticsCombined } from "@/store/analyticsDataStore";
import { DndContext, DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { useTheme } from "next-themes";
import React, { SetStateAction, useEffect, useMemo, useState } from "react";
import {
  filterByDatesTimeSent,
  filterDataGeneric,
  returnKeysToMapGeneric,
} from "../analyticsHelperFunctions";
import { darkAnalyticFills, lightAnalyticFills } from "../AnalyticsStyles";
import { SankeyDataFilter } from "./IndexFlowAnalysis";
import { sankeyKeys } from "./MultiFilterSankey";

export type ExtendedFlowAnalysisDataItem = {
  // eslint-disable-next-line no-unused-vars
  [x in filterNames]: any;
};

function buildDataStream(
  data: IBaseAnalyticsCombined[],
  dataFilters: SankeyDataFilter,
  flowTotalDV0: boolean,
  filterOptions: IFilterOptions[],
) {
  if (!data || data.length == 0) {
    return [];
  }

  const filteredByDates = filterByDatesTimeSent(data, dataFilters);
  //filterDataGeneric is a one pass option to take all filtres from the dataFilters list.
  // filterDataGeneric currently ignores range.
  const activeFilters = {
    currency: dataFilters.selectedCurrencies,
    instrumentType: dataFilters.selectedInstruments,
    structureType: dataFilters.selectedStructure,
    DV01Buckets: dataFilters.selectedBuckets,
    issueCountry: dataFilters.selectedIssueCountry,
    strategyType: dataFilters.selectedStrategy,
    RPCustomer: dataFilters.selectedCustomers,
  };

  const multiFilterResult = filterDataGeneric(filteredByDates, activeFilters);

  const group1TypesObject: Record<string, number> = {};
  const streams = [];

  const grouping1 = filterOptions.find((obj) => obj.activeLocation == 1)!.name;
  const grouping2 = filterOptions.find((obj) => obj.activeLocation == 2)?.name;
  const grouping3 = filterOptions.find((obj) => obj.activeLocation == 3)?.name;

  for (const item of multiFilterResult as ExtendedFlowAnalysisDataItem[]) {
    group1TypesObject[item[grouping1]] =
      (group1TypesObject[item[grouping1]] || 0) +
      (flowTotalDV0 ? Number(item.DV01) : Number(item.notional));
  }

  for (const key in group1TypesObject) {
    if (group1TypesObject.hasOwnProperty(key)) {
      // Recommended to check for own properties
      streams.push({
        from: flowTotalDV0 ? "DV01" : "Notional",
        to: key,
        size: group1TypesObject[key],
      });
    }
  }

  const group1TypesReduced = multiFilterResult.reduce(
    (acc: any, item: ExtendedFlowAnalysisDataItem) => {
      const group1Type = item[grouping1];
      if (!acc[group1Type]) {
        acc[group1Type] = [];
      }
      acc[group1Type].push(item);
      return acc;
    },
    {} as { [key: string]: ExtendedFlowAnalysisDataItem[] },
  );
  const layerTwoObject = {} as {
    [key: string]: ExtendedFlowAnalysisDataItem;
  };
  if (grouping2) {
    // Start on the second layer.
    for (const key in group1TypesReduced) {
      if (group1TypesReduced.hasOwnProperty(key)) {
        layerTwoObject[key] = group1TypesReduced[key].reduce(
          (acc: any, item: ExtendedFlowAnalysisDataItem) => {
            const grouping2Type = item[grouping2];
            if (!acc[grouping2Type]) {
              acc[grouping2Type] = [];
            }
            acc[grouping2Type].push(item);
            return acc;
          },
          {} as { [key: string]: ExtendedFlowAnalysisDataItem[] },
        );
      }
    }
  }

  for (const [key2, value2] of Object.entries(layerTwoObject)) {
    for (const [key2Nested, value2Nested] of Object.entries(value2)) {
      const totalValue = value2Nested.reduce((acc: number, item: any) => {
        return acc + (flowTotalDV0 ? Number(item.DV01) : Number(item.notional));
      }, 0);

      streams.push({
        from: key2,
        to: key2Nested,
        size: totalValue,
      });

      if (grouping3) {
        const thirdLayer = value2Nested.reduce(
          (acc: any, item: IBaseAnalyticsCombined) => {
            const grouping3Type = item[grouping3];
            if (!acc[grouping3Type]) {
              acc[grouping3Type] = [];
            }
            acc[grouping3Type].push(item);
            return acc;
          },
          {} as { [key: string]: IBaseAnalyticsCombined[] },
        );

        for (const [key3, value3] of Object.entries(thirdLayer)) {
          const itemsArray = value3 as IBaseAnalyticsCombined[];
          const totalValueNested = itemsArray.reduce(
            (acc: number, item: IBaseAnalyticsCombined) =>
              acc + (flowTotalDV0 ? Number(item.DV01) : Number(item.notional)),
            0,
          );
          streams.push({
            from: key2Nested,
            to: key3,
            size: totalValueNested,
          });
        }
      }
    }
  }
  return streams;
}

type filterNames =
  | "DV01Buckets"
  | "issueCountry"
  | "instrumentType"
  | "structureType"
  | "strategyType"
  | "RPCustomer"
  | "DV01"
  | "notional";
interface IFilterOptions {
  name: filterNames;
  activeLocation: number;
}

const SankeyFlowChart = ({
  data,
  dataFilters,
  setDataFilters,
}: {
  data: IBaseAnalyticsCombined[];
  dataFilters: SankeyDataFilter;
  setDataFilters: React.Dispatch<SetStateAction<SankeyDataFilter>>;
}) => {
  const { theme } = useTheme();
  const chartRef = React.useRef<any>(null);

  const [flowTotalDV0, setFlowTotalDV0] = useState(false);

  const filterOptionsBase: IFilterOptions[] = [
    { name: "structureType", activeLocation: 3 },
    { name: "strategyType", activeLocation: 0 },
    { name: "RPCustomer", activeLocation: 1 },
    { name: "DV01Buckets", activeLocation: 2 },
    { name: "issueCountry", activeLocation: 0 },
  ];

  const [filterOptions, setFilterOptions] =
    useState<IFilterOptions[]>(filterOptionsBase);

  const [firstActive, setFirstActive] = useState(
    filterOptions.find((item: IFilterOptions) => item.activeLocation == 1)!
      .name,
  );
  const [secondActive, setSecondActive] = useState(
    filterOptions.find((item: IFilterOptions) => item.activeLocation == 2)
      ?.name,
  );
  const [thirdActive, setThirdActive] = useState(
    filterOptions.find((item: IFilterOptions) => item.activeLocation == 3)
      ?.name,
  );

  const [streamData, setStreamData] = useState(
    buildDataStream(data, dataFilters, flowTotalDV0, filterOptions),
  );

  useEffect(() => {
    setStreamData(
      buildDataStream(data, dataFilters, flowTotalDV0, filterOptions),
    );
  }, [data, dataFilters, flowTotalDV0, filterOptions]);

  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  const chartOptions = useMemo<AgChartOptions | any>(() => {
    return {
      background: {
        visible: false,
      },
      theme: theme == "dark" ? "ag-default-dark" : "ag-default",
      // subtitle: {
      //   text: "Flow Analysis",
      // },
      data: streamData,
      listeners: {
        click: () => {
          const {
            instrumentTypeKeys,
            currencyKeys,
            structureTypeKeys,
            DV01BucketsKeys,
            issueCountryKeys,
            strategyTypeKeys,
            RPCustomerKeys,
          } = returnKeysToMapGeneric(data, sankeyKeys);

          setDataFilters({
            ...dataFilters,
            selectedCurrencies: currencyKeys,
            selectedInstruments: instrumentTypeKeys,
            selectedStructure: structureTypeKeys,
            selectedBuckets: DV01BucketsKeys,
            selectedIssueCountry: issueCountryKeys,
            selectedStrategy: strategyTypeKeys,
            selectedCustomers: RPCustomerKeys,
          });
        },
      },
      series: [
        {
          type: "sankey",
          fromKey: "from",
          toKey: "to",
          sizeKey: "size",
          sizeName: "Notional",
          fills: theme == "dark" ? darkAnalyticFills : lightAnalyticFills,
          // node: {
          //   // "click" is part of series.node.events in AG Charts
          //   styles: [
          //     {
          //       match: (node: any) => node.title === selectedNode,
          //       fill: "orange",
          //     },
          //   ],
          // },
          link: {
            fill: theme == "dark" ? "#34495e" : "#9ebede",
            fillOpacity: 0.75,
            stroke: "#2c3e50",
            strokeWidth: 1,
            strokeOpacity: 0.45,
            // styles: [
            //   {
            //     match: (link: any) =>
            //       link.fromKey === selectedNode || link.toKey === selectedNode,
            //     stroke: "orange",
            //     strokeWidth: 3,
            //   },
            // ],
          },

          tooltip: {
            enabled: true,
            renderer: (params: any) => {
              // Determine text color based on theme
              const color = theme === "dark" ? "white" : "black";
              // Keep all your HTML/formatting, just format the number
              if (params.datum == undefined) {
                return `<div style="color: ${color}; font-family: Arial, sans-serif; padding: 10px;">
                  <div style="padding-bottom: 10px"><span>${params.title}</span></div>
                  ${flowTotalDV0 ? `DV01:` : `Notional:`} <span style="padding-left: 5px; margin-top: 10px">$${params.size.toLocaleString()}</span>
                </div>`;
              }
              if (params.datum) {
                return `
                <div style="color: ${color}; font-family: Arial, sans-serif; padding: 10px;">
                  <div style="padding-bottom: 10px"><span>${params.datum.from}</span> → <span>${params.datum.to}</span></div>
                  ${flowTotalDV0 ? `DV01:` : `Notional:`} <span style="padding-left: 5px; margin-top: 10px">$${params.datum.size.toLocaleString()}</span>
                </div>
              `;
              }
            },
          },
          listeners: {
            nodeClick: (event: {
              label: string;
              datum: string;
              seriesId: string;
              title: string;
            }) => {
              // For links, event.datum is defined; for nodes, datum is undefined but title, size are there
              if (event.label) {
                setSelectedNode(event.label);
              }
            },
          },
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, streamData, dataFilters, selectedNode]);

  useEffect(() => {
    // Create a new object that has the range removed from it.
    const reducedFilters = Object.fromEntries(
      Object.entries(dataFilters).filter(([key]) => {
        return key != "range";
      }),
    );

    if (selectedNode) {
      for (const key in reducedFilters) {
        if (!Object.hasOwn(reducedFilters, key)) continue;
        const typedKey = key as keyof SankeyDataFilter;
        const element = reducedFilters[typedKey];
        if (element && element.includes(selectedNode)) {
          // The data has been located on it's relevant array.
          // Need to replace that array in the filter object and update the filter object
          const currentState = reducedFilters[typedKey];
          const newData = currentState.filter((c: string) => c == selectedNode);
          setDataFilters({ ...dataFilters, [key]: newData });
          setSelectedNode(null);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode]);

  function switchFlowTotal(id: "NotionalToggle" | "DV01Toggle") {
    if (id == "NotionalToggle" && flowTotalDV0) {
      setFlowTotalDV0(false);
    } else if (id == "DV01Toggle" && !flowTotalDV0) {
      setFlowTotalDV0(true);
    }
  }

  useEffect(() => {
    setFirstActive(
      filterOptions.find((item) => item.activeLocation == 1)!.name,
    );
    setSecondActive(
      filterOptions.find((item) => item.activeLocation == 2)?.name,
    );
    setThirdActive(
      filterOptions.find((item) => item.activeLocation == 3)?.name,
    );
  }, [filterOptions]);

  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = ({
    setIsDragging,
  }: {
    event: DragStartEvent;
    setIsDragging: React.Dispatch<SetStateAction<boolean>>;
  }) => {
    setIsDragging(true);
  };

  const handleDragEnd = ({
    event,
    setIsDragging,
  }: {
    event: DragEndEvent;
    setIsDragging: React.Dispatch<SetStateAction<boolean>>;
  }) => {
    setIsDragging(false);

    if (event.over) {
      if (event?.over?.id) {
        const overId = Number(event.over.id);

        const currentActive = filterOptions.find(
          (obj) => obj.name === event.active.id,
        );
        if (!currentActive) return;

        const overObj = filterOptions.find(
          (obj) => obj.activeLocation === overId,
        );

        const newOptions = filterOptions.map((obj) => {
          // case 1: swap if both used
          if (
            currentActive.activeLocation !== 0 &&
            overObj &&
            obj.name === currentActive.name
          ) {
            return { ...obj, activeLocation: overObj.activeLocation }; // active takes over spot
          }
          if (
            currentActive.activeLocation !== 0 &&
            overObj &&
            obj.name === overObj.name
          ) {
            return { ...obj, activeLocation: currentActive.activeLocation }; // over takes active's old spot
          }

          // case 2: replace if active was unused
          if (currentActive.activeLocation === 0) {
            if (obj.name === currentActive.name) {
              return { ...obj, activeLocation: overId }; // put active in over's slot
            }
            if (overObj && obj.name === overObj.name) {
              return { ...obj, activeLocation: 0 }; // free up old occupant
            }
          }

          return obj; // unchanged
        });
        setFilterOptions(newOptions);
      }
    }
  };

  function removeSelectedOption(id: number) {
    const newOpitons = filterOptions.map((option) => {
      if (option.activeLocation == id) {
        return { ...option, activeLocation: 0 };
      }
      return { ...option };
    });

    const isOption2 = newOpitons.find((item) => item.activeLocation == 2);
    const isOption3 = newOpitons.find((item) => item.activeLocation == 3);
    if (isOption3 && !isOption2) {
      const newOpitonsAdjusted = newOpitons.map((option) => {
        if (option.activeLocation == 3) {
          return { ...option, activeLocation: 2 };
        }
        return { ...option };
      });
      setFilterOptions(newOpitonsAdjusted);
    } else {
      setFilterOptions(newOpitons);
    }

    // setFilterOptions(newOptions);
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <DndContext
        onDragStart={(event) => {
          handleDragStart({ event, setIsDragging });
        }}
        onDragEnd={(event) => {
          handleDragEnd({ event, setIsDragging });
        }}
      >
        <div className="relative top-2 flex h-5 w-full items-center pl-10">
          <div className="flex flex-1 flex-col">
            <div className="flex">
              <Button
                variantToggle={
                  !flowTotalDV0 ? "selectionActive" : "selectionInactive"
                }
                size={"button_sm"}
                className={`z-20 rounded-l-md`}
                id="NotionalToggle"
                onClick={() => {
                  switchFlowTotal("NotionalToggle");
                }}
              >
                Notional
              </Button>
              <Button
                variantToggle={
                  flowTotalDV0 ? "selectionActive" : "selectionInactive"
                }
                size={"button_sm"}
                className={` z-20 rounded-r-md`}
                id="DV01Toggle"
                onClick={() => {
                  switchFlowTotal("DV01Toggle");
                }}
              >
                DV01
              </Button>
            </div>
          </div>
          <div className="ml-2 flex h-full w-full">
            <div
              id="sankeyFilterOptionSelections"
              className="flex h-full w-full flex-wrap justify-around"
            >
              <div className="flex w-32 justify-center  py-[0.125rem] ">
                <DroppableAnalytics id={"1"} isDragging={isDragging}>
                  <div className="w-32">
                    <DraggableAnalytics
                      id={firstActive}
                      name={firstActive}
                    ></DraggableAnalytics>
                  </div>
                </DroppableAnalytics>
              </div>
              <div className="flex w-32 justify-center py-[0.125rem] ">
                <DroppableAnalytics id={"2"} isDragging={isDragging}>
                  <div className="w-32">
                    {secondActive && (
                      <DraggableAnalytics
                        id={secondActive}
                        name={secondActive}
                      ></DraggableAnalytics>
                    )}
                  </div>
                </DroppableAnalytics>
                {secondActive && (
                  <div className="px-1">
                    <div
                      className="hide-button-fromDrag flex h-5 w-5 items-center justify-center rounded  text-cubeBase-600 hover:cursor-pointer hover:bg-red-500  hover:text-black dark:text-cubeBaseDark-600 hover:dark:bg-red-500 dark:hover:text-white"
                      onClick={() => removeSelectedOption(2)}
                    >
                      {close}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex w-32 justify-center py-[0.125rem] ">
                {secondActive && (
                  <DroppableAnalytics id={"3"} isDragging={isDragging}>
                    <div className="w-32">
                      {thirdActive && (
                        <DraggableAnalytics
                          id={thirdActive}
                          name={thirdActive}
                        ></DraggableAnalytics>
                      )}
                    </div>
                  </DroppableAnalytics>
                )}
                {thirdActive && (
                  <div className="px-1">
                    <div
                      className="hide-button-fromDrag flex h-5 w-5 items-center justify-center rounded  text-cubeBase-600 hover:cursor-pointer hover:bg-red-500  hover:text-black dark:text-cubeBaseDark-600 hover:dark:bg-red-500 dark:hover:text-white"
                      onClick={() => removeSelectedOption(3)}
                    >
                      {close}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <AgCharts
          className={`z-99 relative top-3 ml-[-20%] mr-[-10%] mt-[15px] flex h-[90%] w-[140%]`} // TOP3 needed so the Sankey does not sit over the DND components above it.
          options={chartOptions}
          ref={chartRef}
        />
        <div id="sankeyFilterOptionList" className="absolute top-16 flex w-fit">
          <div className="relative flex flex-col pl-10 pt-[10px] text-sm">
            <div>Filter Options:</div>
            <div className="flex flex-col gap-2 pl-2 pt-2">
              {filterOptions.map((item, index) => {
                if (item.activeLocation == 0) {
                  return (
                    <div className="w-32 border border-solid" key={index}>
                      <DraggableAnalytics
                        id={item.name}
                        name={item.name}
                      ></DraggableAnalytics>
                    </div>
                  );
                }
              })}
            </div>
          </div>
        </div>
      </DndContext>
    </div>
  );
};

export default SankeyFlowChart;
